import { Authorizor } from '../Authorizor';
import { SkeletonLoader } from '../loaders/skeleton-loader/skeleton-loader';

import '../table/table-style.scss';

export interface StaticColumnDef<TData> {
  id: string; // Unique identifier for the column
  header: string | (() => React.ReactNode); // Column header text or render function
  cell: (row: TData) => React.ReactNode; // Function to render cell content
  allowedRoles?: string[]; // Optional: Roles allowed to view this column
  onClick?: (row: TData) => void; // Optional: Click handler for cells in this column
  centerAligned?: boolean; // Optional: Whether the column content should be center aligned
}

export interface StaticTableProps<TData> {
  id?: string; // Unique ID for the table container
  columns: StaticColumnDef<TData>[]; // Column definitions
  data: TData[]; // Array of data to display
  isDataLoading?: boolean; // Boolean to show a loading skeleton while fetching data
  onRowClick?: (row: TData) => void; // Optional: Function to handle row click events
  emptyMessage?: string; // Optional: Custom message when there's no data
  uppercaseHeaders?: boolean; // Optional: Whether header text should be uppercase (default: true)
  getRowStyle?: (row: TData) => React.CSSProperties; // Optional: Function to return inline styles for each row
  getRowClassName?: (row: TData) => string; // Optional: Extra class(es) for each row
  getRowKey?: (row: TData) => string; // Optional: Stable row key (defaults to the row index)
}

const StaticTable = <TData,>({
  id = 'static-table',
  columns,
  data,
  isDataLoading = false,
  onRowClick,
  emptyMessage = 'No results found',
  uppercaseHeaders = true,
  getRowStyle,
  getRowClassName,
  getRowKey,
}: StaticTableProps<TData>) => {
  return (
    <div
      className="table-container static-table-container"
      id={`${id}-container`}
    >
      <div className="table-wrapper">
        <table id={`${id}-table`} className="full-view-table">
          <thead>
            <tr>
              {columns.map((column) => {
                const headerContent = (
                  <th
                    key={column.id}
                    style={{
                      textTransform: uppercaseHeaders ? 'uppercase' : 'none',
                      textAlign: column.centerAligned ? 'center' : 'left',
                      width: column.centerAligned ? '1%' : undefined,
                      whiteSpace: column.centerAligned ? 'normal' : undefined,
                      wordBreak: column.centerAligned
                        ? 'break-word'
                        : undefined,
                      padding: column.centerAligned ? '10px 5px' : undefined,
                    }}
                  >
                    {typeof column.header === 'function'
                      ? column.header()
                      : column.header}
                  </th>
                );

                // Wrap with Authorizor if allowed roles are specified
                return column.allowedRoles?.length ? (
                  <Authorizor
                    key={column.id}
                    allowedRoles={column.allowedRoles}
                  >
                    {headerContent}
                  </Authorizor>
                ) : (
                  headerContent
                );
              })}
            </tr>
          </thead>

          <tbody>
            {isDataLoading ? (
              <tr>
                <td colSpan={columns.length}>
                  <SkeletonLoader count={5} height={30} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-5 text-center">
                  <div className="table-no-results-text">{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={getRowKey ? getRowKey(row) : rowIndex}
                  className={[
                    onRowClick ? 'table-hoverable-row' : '',
                    getRowClassName?.(row) ?? '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={getRowStyle?.(row)}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((column) => {
                    const cellContent = (
                      <td
                        key={column.id}
                        onClick={(e) => {
                          if (column.onClick) {
                            e.stopPropagation(); // Prevent row click when cell has its own handler
                            column.onClick(row);
                          }
                        }}
                        style={{
                          cursor: column.onClick ? 'pointer' : undefined,
                          textAlign: column.centerAligned ? 'center' : 'left',
                          padding: column.centerAligned
                            ? '10px 5px'
                            : undefined,
                        }}
                      >
                        {column.cell(row)}
                      </td>
                    );

                    // Wrap with Authorizor if allowed roles are specified
                    return column.allowedRoles?.length ? (
                      <Authorizor
                        key={column.id}
                        allowedRoles={column.allowedRoles}
                      >
                        {cellContent}
                      </Authorizor>
                    ) : (
                      cellContent
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { StaticTable };
